<template>
  <PageChrome>
    <template #actions>
      <label class="btn secondary importBtn">
        <AppIcon name="upload" :size="16" />
        <span>Import play</span>
        <input
          type="file"
          accept=".json,application/json"
          class="importFileInput"
          @change="handleImport"
        />
      </label>
      <button class="btn secondary" type="button" @click="showNewStrategyPlayModal = true">
        <span class="newPlayPlus">+</span>
        <span>New {{ STRATEGY_PLAY_LABEL }}</span>
      </button>
      <button class="btn primary" type="button" @click="showNewPlayModal = true">
        <span class="newPlayPlus">+</span>
        <span>New play</span>
      </button>
    </template>

    <div class="homePage">
      <section class="hero">
        <h1 class="playsTitle">Plays</h1>
        <p class="heroSub">{{ heroSummary }}</p>
      </section>

      <hr class="hairline" />

      <section class="playsSection">
        <div class="sectionHead">
          <span v-if="!loading">{{ activeCount }} {{ activeCount === 1 ? 'play' : 'plays' }} · {{ runningLabel }}</span>
          <span v-else>Loading plays…</span>
        </div>

        <div v-if="error" class="error">{{ error }}</div>

        <ul v-else-if="plays.length > 0" class="ledger">
          <li v-for="p in plays" :key="p.id">
            <div class="row" :class="{ rowPaused: p.status !== 'active' }" @click="openPlay(p.id)">
              <button class="rowName" type="button" @click="openPlay(p.id)">
                <span class="rowDot" :class="rowDotClass(p)" :aria-label="p.status" />
                <div class="rowNameText">
                  <span class="rowTitle">{{ p.name }}</span>
                  <div class="rowSubRow">
                    <span v-if="p.created_by" class="rowCreator" :title="p.created_by.email">
                      {{ p.created_by.name }}
                    </span>
                    <span v-if="p.engine === 'flock'" class="n-pill flockBadge">{{ STRATEGY_SOURCE_LABEL }}</span>
                    <button v-if="totalApprovals(p) > 0" class="n-pill" type="button" title="Approvals waiting" @click.stop="router.push(`/actions?play=${p.id}`)">
                      <span class="n-pillDot pillDotDanger" />
                      <span class="pillCount">{{ totalApprovals(p) }} {{ totalApprovals(p) === 1 ? 'approval' : 'approvals' }}</span>
                    </button>
                    <span v-else-if="subline(p)" class="rowSub">{{ subline(p) }}</span>
                    <button v-if="(p.pending_human_dials_count || 0) > 0" class="n-pill" type="button" title="Calls waiting to be dialed" @click.stop="router.push(`/dialer/human?play=${p.id}`)">
                      <span class="n-pillDot pillDotCall" />
                      <span class="pillCount">{{ p.pending_human_dials_count }} {{ p.pending_human_dials_count === 1 ? 'call' : 'calls' }}</span>
                    </button>
                    <button v-if="(runningCountsByPlay.get(p.id) || 0) > 0" class="n-pill" type="button" title="Agents running" @click.stop="router.push(`/sales-plays/${p.id}/activity`)">
                      <PulseDot :size="6" color="var(--color-accent)" />
                      <span class="pillCount">{{ runningCountsByPlay.get(p.id) }}</span>
                    </button>
                  </div>
                </div>
              </button>

              <div class="rowCol">
                <span class="rowColLabel">STAGED</span>
                <div class="rowColStats">
                  <button class="rowColStat" type="button" @click.stop="router.push(`/sales-plays/${p.id}/candidates/people?stage=staged`)" title="Staged contacts">
                    <AppIcon name="contact_outline" :size="16" />
                    <StatNumber class="rowColNum" :value="p.staged_contact_count || 0" />
                  </button>
                  <button class="rowColStat rowColStatSecondary" type="button" @click.stop="router.push(`/sales-plays/${p.id}/candidates/companies?stage=staged`)" title="Staged accounts">
                    <AppIcon name="account_outline" :size="14" />
                    <StatNumber class="rowColNum" :value="p.staged_account_count || 0" />
                  </button>
                </div>
              </div>

              <div class="rowCol">
                <span class="rowColLabel">QUALIFIED</span>
                <div class="rowColStats">
                  <button class="rowColStat" type="button" @click.stop="router.push(`/sales-plays/${p.id}/candidates/people?stage=qualified`)" title="Qualified contacts">
                    <AppIcon name="contact_outline" :size="16" />
                    <StatNumber class="rowColNum" :value="p.qualified_contact_count || 0" />
                  </button>
                  <button class="rowColStat rowColStatSecondary" type="button" @click.stop="router.push(`/sales-plays/${p.id}/candidates/companies?stage=qualified`)" title="Qualified accounts">
                    <AppIcon name="account_outline" :size="14" />
                    <StatNumber class="rowColNum" :value="p.qualified_account_count || 0" />
                  </button>
                </div>
              </div>

              <div class="rowCol">
                <span class="rowColLabel">ACTIVE</span>
                <div class="rowColStats">
                  <button class="rowColStat" type="button" @click.stop="router.push(`/sales-plays/${p.id}/candidates/people?stage=active`)" title="Active contacts">
                    <AppIcon name="contact_outline" :size="16" />
                    <StatNumber class="rowColNum" :value="p.enrolled_contact_count || 0" />
                  </button>
                  <button class="rowColStat rowColStatSecondary" type="button" @click.stop="router.push(`/sales-plays/${p.id}/candidates/companies?stage=active`)" title="Active accounts">
                    <AppIcon name="account_outline" :size="14" />
                    <StatNumber class="rowColNum" :value="p.enrolled_account_count || 0" />
                  </button>
                </div>
              </div>

              <div class="rowCol">
                <span class="rowColLabel">ENGAGED</span>
                <div class="rowColStats">
                  <button class="rowColStat" type="button" @click.stop="router.push(`/sales-plays/${p.id}/candidates/people?stage=engaged`)" title="Engaged contacts">
                    <AppIcon name="contact_outline" :size="16" />
                    <StatNumber class="rowColNum" :value="p.engaged_contact_count || 0" primary />
                  </button>
                </div>
              </div>

              <div class="rowActions">
                <button
                  class="rowExport"
                  type="button"
                  title="Export play"
                  aria-label="Export play"
                  @click.stop="handleExport(p)"
                >
                  <AppIcon name="download" :size="16" />
                </button>
                <button class="rowNav" type="button" @click="openPlay(p.id)">
                  <AppIcon class="rowArrow" name="chevron_right" />
                </button>
              </div>
            </div>
          </li>
        </ul>

        <div v-else-if="!loading" class="empty">
          <h2 class="emptyTitle">No plays yet.</h2>
          <p class="emptySub">Plays will appear here once Raven builds them with you.</p>
        </div>
      </section>
    </div>

    <NewSalesPlayModal
      v-if="showNewPlayModal"
      @close="showNewPlayModal = false"
      @created="onPlayCreated"
    />
    <NewStrategyPlayModal
      v-if="showNewStrategyPlayModal"
      @close="showNewStrategyPlayModal = false"
      @created="(play) => openPlay(play.id)"
    />
  </PageChrome>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import PageChrome from '../components/PageChrome.vue'
import AppIcon from '../../components/AppIcon.vue'
import PulseDot from '../../components/PulseDot.vue'
import StatNumber from '../components/StatNumber.vue'
import NewSalesPlayModal from '../components/NewSalesPlayModal.vue'
import NewStrategyPlayModal from '../components/NewStrategyPlayModal.vue'
import { apiFetch } from '../../services/api'
import { useActionCounts } from '../../services/actionCountsPoller'
import { useChatScope } from '../composables/useChatScope'
import { STRATEGY_PLAY_LABEL, STRATEGY_SOURCE_LABEL, type PlayEngine } from '../composables/usePlayEngine'

interface PlaySummary {
  id: string
  name: string
  description: string | null
  status: string                 // 'active' | 'disabled' | 'archived'
  engine?: PlayEngine
  agent_id: string | null
  active_account_count: number
  staged_account_count: number
  active_contact_count: number
  staged_contact_count: number
  qualified_account_count: number
  qualified_contact_count: number
  enrolled_account_count: number
  enrolled_contact_count: number
  engaged_account_count: number
  engaged_contact_count: number
  pending_reachouts_count: number
  pending_human_dials_count: number
  created_at: string
  updated_at: string
  created_by: { id: string; email: string; name: string } | null
}

const router = useRouter()
const { setScope, clearScope } = useChatScope()
const { activeRuns } = useActionCounts()

const plays = ref<PlaySummary[]>([])
const loading = ref(true)
const error = ref('')

// Pre-bucket active runs by sales_play_id so each row read is O(1) and
// no per-row computed leaks reactive effects.
const runningCountsByPlay = computed<Map<string, number>>(() => {
  const m = new Map<string, number>()
  for (const r of activeRuns.value as { sales_play_id: string | null; status: string }[]) {
    if (!r.sales_play_id || r.status !== 'running') continue
    m.set(r.sales_play_id, (m.get(r.sales_play_id) || 0) + 1)
  }
  return m
})

const activeCount = computed(() => plays.value.filter((p) => p.status === 'active').length)

const runningLabel = computed(() => {
  const n = activeCount.value
  if (n === 0) return 'none running'
  return `${n} running`
})

const heroSummary = computed(() => {
  if (loading.value) return 'Loading your plays…'
  const running = activeCount.value
  if (plays.value.length === 0) return 'No plays yet. Raven will help you build the first one.'
  const enrolled = plays.value.reduce(
    (sum, p) => sum + (p.enrolled_contact_count || 0),
    0,
  )
  const engaged = plays.value.reduce(
    (sum, p) => sum + (p.engaged_contact_count || 0),
    0,
  )
  return `${running} ${running === 1 ? 'play' : 'plays'} running · ${enrolled} ${enrolled === 1 ? 'person' : 'people'} active · ${engaged} ${engaged === 1 ? 'person' : 'people'} engaged`
})

function rowDotClass(p: PlaySummary): string {
  if (p.status === 'active')   return 'rowDotRunning'
  if (p.status === 'archived') return 'rowDotArchived'
  return 'rowDotPaused'
}

function totalApprovals(p: PlaySummary): number {
  return p.pending_reachouts_count || 0
}

function subline(p: PlaySummary): string {
  return p.status !== 'active' ? 'Disabled' : ''
}

function openPlay(id: string) {
  router.push(`/sales-plays/${id}`)
}

// ── New play modal ───────────────────────────────────────────────────
const showNewPlayModal = ref(false)
const showNewStrategyPlayModal = ref(false)

async function onPlayCreated() {
  showNewPlayModal.value = false
  await fetchPlays(true)
}

// ── Export ───────────────────────────────────────────────────────────
async function handleExport(p: PlaySummary) {
  try {
    const blob = await apiFetch(`/sales-plays/${p.id}/export`, { responseType: 'blob' })
    const slug = String(p.name || '').trim().toLowerCase()
      .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
    const filename = `${slug || p.id}.json`
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  } catch (e: any) {
    alert(e?.message || 'Export failed')
  }
}

// ── Import ───────────────────────────────────────────────────────────
async function handleImport(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) return
  // Reset so the same file can be re-imported.
  input.value = ''
  const form = new FormData()
  form.append('file', file)
  try {
    await apiFetch('/sales-plays/import', { method: 'POST', body: form })
    await fetchPlays(true)
  } catch (e: any) {
    alert(e?.message || 'Import failed')
  }
}

// ── Data fetch + polling ──────────────────────────────────────────────
// Mirrors the SalesPlaysPage cadence (silent re-fetch every few seconds
// so chat-driven changes show up without a manual refresh).
let pollHandle: number | undefined

async function fetchPlays(silent = false) {
  if (!silent) {
    loading.value = true
    error.value = ''
  }
  try {
    const resp: any = await apiFetch('/sales-plays')
    plays.value = resp?.plays || []
    if (silent) error.value = ''
  } catch (e: any) {
    if (!silent) error.value = e?.message || 'Failed to load plays'
  } finally {
    if (!silent) loading.value = false
  }
}

onMounted(async () => {
  // Tell the AppShell-level chat that we're at workspace scope. The chat
  // panel reads this via useChatScope and forwards as props to ChatAgent.
  setScope({ scopeType: 'workspace', scopeSubjectId: '', pageContext: null })

  await fetchPlays(false)
  pollHandle = window.setInterval(() => fetchPlays(true), 5000)
})

onBeforeUnmount(() => {
  if (pollHandle !== undefined) window.clearInterval(pollHandle)
  clearScope()
})
</script>

<style scoped>
/* ── Page-header slots ─────────────────────────────────────── */

.importBtn { cursor: pointer; position: relative; }
.importFileInput {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}

.newPlayPlus {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--space-3);
  height: var(--space-3);
  font-weight: var(--fw-semibold);
}

/* ── Hero ──────────────────────────────────────────────────── */
.hero {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  margin-top: var(--space-4);
  margin-bottom: var(--space-7);
}
.playsTitle {
  margin: 0;
  font-family: var(--font-display);
  font-size: var(--fs-xl);
  font-weight: var(--fw-semibold);
  letter-spacing: var(--tracking-tight);
  line-height: var(--lh-tight);
  color: var(--color-text-primary);
}
.heroSub {
  margin: 0;
  max-width: 640px;
  font-family: var(--font-sans);
  font-size: var(--fs-base);
  font-weight: var(--fw-regular);
  line-height: var(--lh-loose);
  color: var(--color-text-secondary);
}

.hairline {
  border: 0;
  border-top: 1px solid var(--color-border-subtle);
  margin: 0;
}

/* ── Plays section ─────────────────────────────────────────── */
.playsSection { margin-top: var(--space-7); }
.sectionHead {
  margin-bottom: var(--space-3);
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  letter-spacing: var(--tracking-wide);
  line-height: var(--lh-tight);
  text-transform: uppercase;
  color: var(--color-text-secondary);
}

.error {
  padding: var(--space-4);
  background: var(--color-danger-soft);
  border-radius: var(--radius-md);
  font-family: var(--font-sans);
  font-size: var(--fs-base);
  font-weight: var(--fw-regular);
  line-height: var(--lh-normal);
  color: var(--color-danger);
}

/* Ledger rows — hairline separated. */
.ledger { list-style: none; padding: 0; margin: 0; }
.ledger li + li { border-top: 1px solid var(--color-border-subtle); }

.row {
  position: relative;
  display: grid;
  grid-template-columns: minmax(280px, 3fr) repeat(4, minmax(80px, 1fr)) auto;
  align-items: center;
  gap: var(--space-4);
  width: 100%;
  padding: var(--space-4) var(--space-3);
  border-radius: var(--radius-md);
  color: var(--color-text-primary);
  cursor: pointer;
  transition: background var(--duration-fast) var(--ease-out);
}
.row:hover { background: var(--color-bg-subtle); }
.row:hover .rowArrow { color: var(--color-accent); transform: translateX(2px); }
.row.rowPaused { opacity: 0.65; }
.row.rowPaused:hover { opacity: 0.85; }

.rowName {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  min-width: 0;
  background: none;
  border: 0;
  padding: 0;
  text-align: left;
  cursor: pointer;
  color: inherit;
}
.rowDot {
  width: var(--space-2);
  height: var(--space-2);
  border-radius: var(--radius-full);
  flex-shrink: 0;
}
.rowDotRunning  { background: var(--color-accent); }
.rowDotPaused   { background: var(--color-text-tertiary); }
.rowDotArchived { background: var(--color-border-strong); }

.rowNameText {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}
.rowTitle {
  font-family: var(--font-sans);
  font-size: var(--fs-md);
  font-weight: var(--fw-semibold);
  letter-spacing: var(--tracking-tight);
  line-height: var(--lh-tight);
  color: var(--color-text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.rowSubRow {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  min-width: 0;
}
/* Pill base styles come from next/styles.css (.n-pill / .n-pillDot). */
.pillDotDanger { background: var(--color-danger); }
.pillDotCall { background: #E8A33D; }
.pillCount { font-variant-numeric: tabular-nums; }
.rowSub {
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  font-weight: var(--fw-regular);
  line-height: var(--lh-tight);
  color: var(--color-text-tertiary);
}
.rowCreator {
  font-family: var(--font-sans);
  font-size: var(--fs-xs);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-tight);
  color: var(--color-text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

/* Stat columns — STAGED / QUALIFIED / ENROLLED / ENGAGED */
.rowCol {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  min-width: 0;
  padding-left: var(--space-5);
}
.rowCol + .rowCol {
  border-left: 1px solid var(--color-border-subtle);
}
.rowColLabel {
  font-family: var(--font-sans);
  font-size: 10px;
  font-weight: var(--fw-semibold);
  letter-spacing: var(--tracking-wide);
  text-transform: uppercase;
  color: var(--color-text-tertiary);
  line-height: 1;
}
.rowColStats {
  display: flex;
  align-items: baseline;
  gap: var(--space-4);
}
.rowColStat {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: 0;
  padding: 0;
  cursor: pointer;
  color: var(--color-text-primary);
  font-family: var(--font-sans);
  line-height: 1;
  border-radius: var(--radius-sm);
  transition: color var(--duration-fast) var(--ease-out);
}
.rowColStat:hover { color: var(--color-accent); }
.rowColNum {
  font-size: var(--fs-lg);
  font-weight: var(--fw-semibold);
  font-variant-numeric: tabular-nums;
}
.rowColStatSecondary {
  color: var(--color-text-tertiary);
}
.rowColStatSecondary .rowColNum {
  font-size: var(--fs-base);
  font-weight: var(--fw-regular);
}

.rowActions {
  display: inline-flex;
  align-items: center;
  gap: 0;
}
.rowNav {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: var(--radius-md);
  background: transparent;
  cursor: pointer;
  color: inherit;
}
.rowExport {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: var(--radius-sm);
  background: transparent;
  cursor: pointer;
  color: var(--color-text-tertiary);
  opacity: 0;
  pointer-events: none;
  transition:
    opacity var(--duration-fast) var(--ease-out),
    color var(--duration-fast) var(--ease-out),
    background var(--duration-fast) var(--ease-out);
}
.row:hover .rowExport,
.rowExport:focus-visible {
  opacity: 1;
  pointer-events: auto;
}
.rowExport:hover {
  color: var(--color-accent);
  background: var(--color-bg-subtle);
}
.rowArrow {
  width: var(--space-4);
  height: var(--space-4);
  color: var(--color-text-tertiary);
  transition:
    color var(--duration-fast) var(--ease-out),
    transform var(--duration-fast) var(--ease-premium);
}

/* ── Empty state ───────────────────────────────────────────── */
.empty {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-7) 0;
}
.emptyTitle {
  margin: 0;
  font-family: var(--font-display);
  font-size: var(--fs-xl);
  font-weight: var(--fw-semibold);
  letter-spacing: var(--tracking-tight);
  line-height: var(--lh-tight);
  color: var(--color-text-primary);
}
.emptySub {
  margin: 0;
  font-family: var(--font-sans);
  font-size: var(--fs-base);
  font-weight: var(--fw-regular);
  line-height: var(--lh-normal);
  color: var(--color-text-secondary);
}

/* ─── New play modal ─── (form styles live in NewSalesPlayModal.vue) */
.newPlayLevelRow { display: flex; gap: var(--space-2); }
.newPlayLevelBtn {
  flex: 1;
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-md);
  background: var(--color-bg-app);
  color: var(--color-text-secondary);
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  font-weight: var(--fw-medium);
  cursor: pointer;
  transition: border-color var(--duration-fast) var(--ease-out),
              color var(--duration-fast) var(--ease-out);
}
.newPlayLevelBtn:hover { border-color: var(--color-text-tertiary); }
.newPlayLevelBtnActive {
  border-color: var(--color-accent);
  color: var(--color-accent);
  font-weight: var(--fw-semibold);
}
</style>
